import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, FormLabel, Switch } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { buildValuesTab } from '../WidgetEditDialog/buildValuesTab';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getValueKey, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, joinValues, loadValuesIfNeeded, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { getItemLayoutSx } from '../WidgetFormRadio/Utils/getItemLayoutSx';
import { type } from './types';
const WidgetFormCheckboxList = (props) => {
    var _a, _b, _c;
    const values = (_b = (_a = props.config.values) !== null && _a !== void 0 ? _a : props.internalData.values) !== null && _b !== void 0 ? _b : [];
    const error = getVisibleError(props);
    const data = getData(props);
    const CheckboxOrSwitch = ((_c = props.config.style) === null || _c === void 0 ? void 0 : _c.type) === 'switch' ? Switch : Checkbox;
    const formLabelSuffix = getFormLabelSuffix(props);
    const ariaLabelledBy = `checkbox-title-${props.id}`;
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, error: !!error, disabled: props.config.disabled, sx: { display: 'flex', alignItems: 'stretch' } },
        props.config.label && (React.createElement(FormLabel, { id: ariaLabelledBy, sx: { mb: 1 }, error: !!error, disabled: props.config.disabled }, formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label)),
        props.internalData.loading && React.createElement(CircularProgress, { color: 'inherit', size: 20 }),
        React.createElement(FormGroup, { role: 'group', "aria-labelledby": ariaLabelledBy, sx: getItemLayoutSx(props.config.style) }, values.map((value, index) => {
            var _a;
            const checked = props.config.valueEquals
                ? data.some((dataValue) => props.config.valueEquals && props.config.valueEquals(dataValue, value.value))
                : data.includes(value.value);
            const checkbox = (React.createElement(CheckboxOrSwitch, { checked: checked, onChange: () => {
                    const data = getData(props);
                    const index = data.findIndex((dataValue) => {
                        if (props.config.valueEquals) {
                            return props.config.valueEquals(dataValue, value.value);
                        }
                        return dataValue === value.value;
                    });
                    if (index === -1) {
                        data.push(value.value);
                    }
                    else {
                        data.splice(index, 1);
                    }
                    setData(props, data);
                    renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                }, color: 'primary', value: value.value, disabled: props.config.disabled || value.disabled, inputProps: Object.assign(Object.assign({}, (props.editMode ? { tabIndex: -1 } : {})), { 'aria-describedby': ariaDescribedBy }), autoFocus: ((_a = (!props.editMode && props.config.autoFocus)) !== null && _a !== void 0 ? _a : false) && index === 0 }));
            return (React.createElement(FormControlLabel, { key: getValueKey(value.value), control: checkbox, label: value.label, disabled: value.disabled }));
        })),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormCheckboxListEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, {
            title: 'Checkbox List: ' + props.config.label,
        }, buildGeneralTab(props, {
            key: true,
            labelOptional: true,
            appendRowIndexToLabel: true,
            description: true,
            disabled: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.checkbox',
                    key: 'dataAsObject',
                    label: 'Data As Object',
                },
            ],
        }), buildValuesTab(props), buildValidateTab(props, { required: true }), buildHiddenTab(props), buildStyleTab(props, {
            align: true,
            children: [
                {
                    type: 'form.radio',
                    key: 'style.layout',
                    label: 'Layout',
                    values: [
                        { value: 'column', label: 'Column' },
                        { value: 'inline', label: 'Inline' },
                        { value: '2-columns-fit', label: '2 Columns Fit' },
                        { value: '2-columns-stretch', label: '2 Columns Stretch' },
                    ],
                    style: { layout: 'inline' },
                },
                {
                    type: 'form.content',
                    text: 'With this combination of align and layout styles checkboxes will not be aligned vertically.',
                    style: {
                        alert: 'warning',
                    },
                    hidden: {
                        custom: (handler) => {
                            var _a, _b;
                            const align = (_a = handler.up().down('style.align')) === null || _a === void 0 ? void 0 : _a.getData();
                            const layout = (_b = handler.up().down('style.layout')) === null || _b === void 0 ? void 0 : _b.getData();
                            return !(layout === '2-columns-stretch' && (align === 'center' || align === 'right'));
                        },
                    },
                },
            ],
            onLoad: (data) => {
                var _a, _b;
                if (((_a = data.style) === null || _a === void 0 ? void 0 : _a.layout) == null && ((_b = data.style) === null || _b === void 0 ? void 0 : _b.inline)) {
                    data.layout = 'inline';
                }
                return data;
            },
        }), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormCheckboxList, Object.assign({}, props))));
};
export const widgetFormCheckboxList = {
    type,
    init: (props) => {
        props.internalData.loadValues = true;
        loadValuesIfNeeded(props, true, props.config.dataAsObject);
    },
    validate: (props) => {
        var _a, _b;
        if (isSkipValidate(props)) {
            return;
        }
        const values = getData(props);
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (values.length === 0) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        return validateCustom(props, values);
    },
    update: (props) => {
        loadValuesIfNeeded(props, true, props.config.dataAsObject);
        return handleWidgetUpdate(props);
    },
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        var _a, _b;
        const value = getData(props);
        if (value != null) {
            const values = (_b = (_a = props.config.values) !== null && _a !== void 0 ? _a : props.internalData.values) !== null && _b !== void 0 ? _b : [];
            return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), joinValues(values.filter((v) => value.includes(v.value)).map((v) => v.label)));
        }
        return [];
    },
    component: WidgetFormCheckboxList,
    icon: React.createElement(FormatListBulletedIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormCheckboxListEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'checkboxList', label: 'Checkbox List' }),
};
function getData(props) {
    const data = props.getData();
    if (Array.isArray(data)) {
        return data;
    }
    else if (props.config.dataAsObject && typeof data === 'object') {
        return Object.entries(data)
            .filter(([, value]) => !!value)
            .map(([key]) => key);
    }
    else {
        return [];
    }
}
function setData(props, data) {
    if (props.config.dataAsObject) {
        const object = {};
        for (const item of data) {
            if (typeof item === 'string') {
                object[item] = true;
            }
        }
        props.setData(object);
    }
    else {
        props.setData(data);
    }
}
